import { all, call, put, takeLatest } from 'redux-saga/effects';
import ApiInstance from 'src/helpers/apiInstance';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import { isSuccess } from 'src/containers/admin/pricing/rateEngine/rateEngineShared/apiStatus';
import { CONTRACT_ACTIONS } from '../constants';
import { toPathTemplate } from '../shared/actionHelpers';
import { isEngineBoundBody } from '../sharedTypes';
import actions from './actions';
import actionTypes from './actionTypes';
import type { ILoadContractRequestPayload } from './types';

const CONTRACT_PATH = 'agency_contracts/:id';

const contractApi = new ApiInstance(CONTRACT_PATH);

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;

type LoadContractAction = { type: string; payload: ILoadContractRequestPayload };

export function* loadContract({ payload }: LoadContractAction): Generator<any, void, any> {
  try {
    // A tab is opened by following its own action url; the page's entry point is the Current slot,
    // which is the only one addressable without an action.
    const { version } = payload;
    const path = version ? toPathTemplate(version.url) : CONTRACT_PATH;
    const request = version ? new ApiInstance(path).get : contractApi.get;
    const args = version ? {} : { pathParams: { id: payload.id } };
    const result = yield call(sendRequest, request, args);

    if (isSuccess(result) && result.data?.id) {
      // The pages key the slice on the id they asked for; a body answering under another id is still
      // shown, but the mismatch is a contract break worth hearing about.
      if (String(result.data.id) !== payload.id) {
        logger.error(new Error(`[agencyContractsV2] ${path} answered id ${result.data.id} for ${payload.id}`));
      }

      // The bare route serves both worlds and the server picks the body from the row, so the body
      // is what says which page to render — the per-country flag would be wrong here.
      if (!isEngineBoundBody(result.data)) {
        yield put(actions.loadContractLegacy());

        return;
      }

      yield put(actions.loadContractSuccess(result.data, version?.tab ?? CONTRACT_ACTIONS.CURRENT_SHOW));

      return;
    }

    // A 403 (country without the pricing flag, or an unpermitted role) and a 404 both come with a
    // sentence worth showing instead of "try again".
    const explained = result?.status === HTTP_FORBIDDEN || result?.status === HTTP_NOT_FOUND;

    logger.error(new Error(`[agencyContractsV2] ${path} answered ${result?.status ?? 'network error'}`));

    yield put(
      actions.loadContractError(result?.status === HTTP_NOT_FOUND, explained ? result?.messages?.[0] ?? null : null),
    );
  } catch (error) {
    logger.error(error);

    yield put(actions.loadContractError());
  }
}

// `takeLatest`: switching tabs quickly must not let a superseded amendment's body land last.
export default function* rootSaga() {
  yield all([takeLatest(actionTypes.LOAD_CONTRACT_REQUEST, loadContract)]);
}
